import React, { useCallback, useState } from 'react';

import { InlineAlert, Typography } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { initialErrorState } from 'features/payment/initial-state';
import { CCFormField, CardType } from 'features/payment/types';
import { mapPaymentMethodCardTypes, onCCFormChange } from 'features/payment/utils';

import PaymentMethodSelect, { HoverTheme } from 'components/payment-method-select';
import { PrepaidBalanceInfo } from 'components/prepaid-balance-info';
import CreditCardFormInputs from 'experiments/checkout-v2/control/components/credit-card-form-inputs';
import { usePrepaidsPaymentMethods } from 'hooks/prepaid/use-prepaids-payment-methods';
import { emptyCartContext, useCartContext } from 'state/cart';
import { t, tRich } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { SupportedCardType, defaultSupportedCardTypes } from 'state/launchdarkly/variations';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { InstructionText, LoadContainer, ReloadWrapper } from './styled';
import { IFullPrepaidMethodReloadProps } from './types';

const PrepaidMethodReload: React.FC<React.PropsWithChildren<IFullPrepaidMethodReloadProps>> = ({
  isDelivery,
  reloadAmount,
  reloadPaymentValues,
  setReloadPaymentValues,
  orderTotal,
}) => {
  const [errors, setErrors] = useState(initialErrorState);

  const { formatCurrencyForUser } = useUIContext();
  const { setPrepaidReloadPaymentMethodId, getBalanceFromPaymentMethods } = usePaymentContext();
  const {
    nonSelectedReloadPaymentMethods,
    reloadPaymentMethods,
    selectedReloadPaymentMethod,
  } = usePrepaidsPaymentMethods();
  const router = useRouter();

  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];

  const formattedReloadAmount = formatCurrencyForUser(reloadAmount);

  const onChange = useCallback(
    (name: CCFormField, value: string) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        reloadPaymentValues,
        errors,
        supportedCardTypes
      );
      setErrors(prevState => ({ ...prevState, ...formErrors }));
      setReloadPaymentValues(prevState => ({ ...prevState, ...state }));
    },
    [reloadPaymentValues, errors, setReloadPaymentValues, supportedCardTypes]
  );

  const reloadExplanationText = tRich('reloadExplanation.bk', {
    reloadAmount: (
      <Typography fontFamily="heading" fontSize="xs">
        {formattedReloadAmount}
      </Typography>
    ),
  });

  const youWillBeChargedText = t('youWillBeCharged', { reloadAmount: formattedReloadAmount });

  const isInCart = useCartContext() !== emptyCartContext;

  const instructionKey = reloadPaymentMethods.length ? 'loadWith' : 'addCreditCard';
  const instructionText = t(instructionKey);

  const prepaidBalance = selectedReloadPaymentMethod
    ? getBalanceFromPaymentMethods(selectedReloadPaymentMethod)
    : 0;

  return (
    <ReloadWrapper>
      <PrepaidBalanceInfo prepaidBalance={prepaidBalance} orderTotal={orderTotal} />
      <InlineAlert
        message={reloadExplanationText}
        status="warning"
        size="sm"
        style={{ marginTop: 16 }}
        alertContainerStyles={{ paddingX: 2 }}
      />
      <LoadContainer>
        <InstructionText>{instructionText}:</InstructionText>
        <Typography style={{ marginBottom: 8, fontSize: 12 }}>{youWillBeChargedText}</Typography>
        {reloadPaymentMethods.length ? (
          <PaymentMethodSelect
            onOptionClick={setPrepaidReloadPaymentMethodId}
            nonSelectedMethods={nonSelectedReloadPaymentMethods}
            selectedMethod={selectedReloadPaymentMethod}
            hoverTheme={HoverTheme.LIGHT}
            onNavigatePress={() => router.navigate(`${routes.addCard}?isInCart=${isInCart}`)}
          />
        ) : (
          <CreditCardFormInputs
            ariaLabel="credit-card-form-inputs"
            isDelivery={isDelivery}
            onChange={onChange}
            paymentValues={reloadPaymentValues}
            errors={errors}
            withBorder={false}
          />
        )}
      </LoadContainer>
    </ReloadWrapper>
  );
};

export default PrepaidMethodReload;
